/**
 * Service che orchestra il flusso di iscrizione marketplace.
 *
 * Pattern allineato a benvenuto/LeadRegistrationService:
 *  1. Client autentica l'utente (Email/Password creando l'account, OPPURE Google sign-in)
 *  2. Client chiama CF AUTENTICATA registerMarketplaceTenant con dati azienda
 *  3. Client manda email verifica via Firebase Auth built-in
 *  4. Client redirect a /verifica-email
 *
 * Vecchia funzione `performPublicSignup` deprecata (lasciata per backward compat
 * ma non piu' usata dal wizard).
 */

import { httpsCallable } from 'firebase/functions';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { getMarketplaceAuth, getMarketplaceFunctions, functions } from '../firebase';
import type { SignupState } from '../pages/signup/types';
import { TERMINI_VERSIONE_CORRENTE, PRIVACY_VERSIONE_CORRENTE } from '../data/legal/currentVersions';

// ============================================================================
// Tipi
// ============================================================================

export interface RegisterTenantPayload {
  /** Tier scelto al signup (default 'free') — signup unificato 2026-05-20 */
  tier?: 'free' | 'base' | 'pro' | 'business';
  /** Mensile/annuale, rilevante solo per tier paid */
  billing_frequency?: 'monthly' | 'yearly';
  /** OPZIONALE dal 2026-05-18: se presente, attiva welcome bonus addon marketplace */
  ruolo?: 'venditore' | 'installatore' | 'entrambi' | 'fornitore';
  ragione_sociale: string;
  partita_iva: string;
  sede_via: string;
  sede_citta: string;
  sede_cap: string;
  sede_provincia: string;
  sede_regione: string;
  admin_telefono: string;
  accettato_termini: boolean;
  accettato_privacy: boolean;
  accettato_marketing?: boolean;
  signup_method: 'email_password' | 'google';
  locale?: 'it' | 'en';
  terms_version?: string;
  privacy_version?: string;
}

export interface RegisterTenantResponse {
  success: true;
  tenant_id: string;
  uid: string;
  email: string;
  email_verified: boolean;
  app_login_url: string;
  tier: 'free' | 'base' | 'pro' | 'business';
  trial_ends_at: string | null;
}

export interface SignupResult {
  ok: true;
  tenant_id: string;
  uid: string;
  email: string;
  /** true se l'utente arriva da provider OAuth (Google) che fornisce email gia verificata */
  email_verified: boolean;
  app_login_url: string;
  /** Tier effettivamente assegnato al tenant */
  tier: 'free' | 'base' | 'pro' | 'business';
  /** ISO timestamp scadenza trial (solo per tier paid, null per free) */
  trial_ends_at: string | null;
}

export interface SignupError {
  ok: false;
  error_code: string;
  message?: string;
}

// ============================================================================
// Email/password sign-up (lato client)
// ============================================================================

/**
 * Crea l'utente Firebase Auth con email + password.
 * Da chiamare nel wizard al click "Conferma e iscriviti" SE l'utente
 * NON e' venuto da Google (cioe' non e' gia loggato).
 */
export async function createEmailPasswordUser(
  email: string,
  password: string,
  displayName: string,
): Promise<{ ok: true; uid: string } | SignupError> {
  const auth = getMarketplaceAuth();
  try {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(credential.user, { displayName });
    }
    return { ok: true, uid: credential.user.uid };
  } catch (err: unknown) {
    if (err instanceof FirebaseError) {
      if (err.code === 'auth/email-already-in-use') {
        return { ok: false, error_code: 'auth.email.already_in_use' };
      }
      if (err.code === 'auth/invalid-email') {
        return { ok: false, error_code: 'auth.email.invalid' };
      }
      if (err.code === 'auth/weak-password') {
        return { ok: false, error_code: 'auth.password.weak' };
      }
      return { ok: false, error_code: 'auth.create.unknown', message: err.message };
    }
    return { ok: false, error_code: 'auth.create.unknown' };
  }
}

/**
 * Login con email/password (per utenti che hanno gia un account dal flusso Email).
 * Usato come fallback se createUserWithEmailAndPassword fallisce con email_already_in_use
 * E l'utente conferma di voler usare la vecchia password.
 */
export async function signInEmailPassword(
  email: string,
  password: string,
): Promise<{ ok: true; uid: string } | SignupError> {
  const auth = getMarketplaceAuth();
  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return { ok: true, uid: credential.user.uid };
  } catch (err: unknown) {
    if (err instanceof FirebaseError) {
      return { ok: false, error_code: 'auth.login.failed', message: err.message };
    }
    return { ok: false, error_code: 'auth.login.unknown' };
  }
}

// ============================================================================
// Register marketplace tenant (CF call)
// ============================================================================

export async function registerMarketplaceTenant(
  payload: RegisterTenantPayload,
): Promise<{ ok: true; data: RegisterTenantResponse } | SignupError> {
  try {
    // IMPORTANTE: usa l'istanza Functions collegata a marketplace-auth, perche'
    // httpsCallable allega l'idToken dell'utente loggato sull'app Functions.
    // L'utente del marketplace e' su getMarketplaceAuth(), non sul default app.
    const callable = httpsCallable<RegisterTenantPayload, RegisterTenantResponse>(
      getMarketplaceFunctions(),
      'registerMarketplaceTenant',
    );
    const result = await callable(payload);
    return { ok: true, data: result.data };
  } catch (err: unknown) {
    const message = (err as { message?: string }).message || '';
    const code = (err as { code?: string }).code || '';
    return {
      ok: false,
      error_code: message || code || 'register.unknown_error',
    };
  }
}

// ============================================================================
// Send email verification
// ============================================================================

export async function sendVerificationEmail(redirectUrl: string): Promise<void> {
  const auth = getMarketplaceAuth();
  if (!auth.currentUser) return;
  try {
    await sendEmailVerification(auth.currentUser, {
      url: redirectUrl,
      handleCodeInApp: false,
    });
  } catch (err) {
    console.warn('[SignupService] sendEmailVerification failed', err);
  }
}

// ============================================================================
// Orchestratore: completa il signup end-to-end
// ============================================================================

/**
 * Flusso end-to-end:
 *  1. Se NON gia loggato: createUserWithEmailAndPassword
 *     (l'utente venuto da Google e' gia loggato sulla marketplace-auth)
 *  2. Chiama registerMarketplaceTenant con dati azienda
 *  3. Manda email verifica
 */
export async function completeMarketplaceSignup(
  state: SignupState,
  signupMethod: 'email_password' | 'google',
  locale: 'it' | 'en' = 'it',
): Promise<SignupResult | SignupError> {
  const auth = getMarketplaceAuth();

  // Step 1: assicurati che l'utente sia autenticato
  if (signupMethod === 'email_password') {
    if (!state.admin_email || !state.admin_password) {
      return { ok: false, error_code: 'auth.email.missing_credentials' };
    }
    if (!auth.currentUser) {
      const displayName = `${state.admin_nome ?? ''} ${state.admin_cognome ?? ''}`.trim();
      const created = await createEmailPasswordUser(
        state.admin_email,
        state.admin_password,
        displayName,
      );
      if (!created.ok) return created;
    }
    // dopo createUser, l'utente e' loggato automaticamente
  } else {
    // signup_method === 'google'
    if (!auth.currentUser) {
      return { ok: false, error_code: 'auth.google.session_lost' };
    }
  }

  // Sanity check: deve esistere un currentUser
  if (!auth.currentUser) {
    return { ok: false, error_code: 'auth.no_current_user' };
  }

  // Step 2: chiama CF registerMarketplaceTenant
  const payload: RegisterTenantPayload = {
    // Tier scelto al signup (default 'free' se non specificato)
    tier: state.tier ?? 'free',
    billing_frequency: state.billing_frequency ?? 'monthly',
    // ruolo passato solo se l'utente l'ha esplicitamente dichiarato
    // (oggi il wizard NON lo chiede piu', ma manteniamo il campo per
    // landing future che vogliano specializzare un welcome bonus).
    ...(state.ruolo ? { ruolo: state.ruolo } : {}),
    ragione_sociale: state.ragione_sociale?.trim() ?? '',
    partita_iva: state.partita_iva?.trim() ?? '',
    sede_via: state.sede_via?.trim() ?? '',
    sede_citta: state.sede_citta?.trim() ?? '',
    sede_cap: state.sede_cap?.trim() ?? '',
    sede_provincia: state.sede_provincia?.trim() ?? '',
    sede_regione: state.sede_regione?.trim() ?? '',
    admin_telefono: state.admin_telefono?.trim() ?? '',
    accettato_termini: !!state.accettato_termini,
    accettato_privacy: !!state.accettato_privacy,
    accettato_marketing: !!state.accettato_marketing,
    signup_method: signupMethod,
    locale,
    terms_version: TERMINI_VERSIONE_CORRENTE,
    privacy_version: PRIVACY_VERSIONE_CORRENTE,
  };

  const cfResult = await registerMarketplaceTenant(payload);
  if (!cfResult.ok) return cfResult;

  // Step 3: manda email verifica (solo se non gia verified, es. Google la fornisce gia verified per workspace)
  if (!cfResult.data.email_verified) {
    await sendVerificationEmail(cfResult.data.app_login_url + '?email_verified=1');
  }

  return {
    ok: true,
    tenant_id: cfResult.data.tenant_id,
    uid: cfResult.data.uid,
    email: cfResult.data.email,
    email_verified: cfResult.data.email_verified,
    app_login_url: cfResult.data.app_login_url,
    tier: cfResult.data.tier ?? state.tier ?? 'free',
    trial_ends_at: cfResult.data.trial_ends_at ?? null,
  };
}

// ============================================================================
// VAT lookup (auto-compile via VIES) — invariato
// ============================================================================

export interface VatLookupResult {
  ok: true;
  ragione_sociale?: string;
  indirizzo_completo?: string;
  parsed?: {
    via?: string;
    cap?: string;
    citta?: string;
    provincia?: string;
    regione?: string;
  };
}

export interface VatLookupError {
  ok: false;
  error_code: string;
}

export async function lookupVat(partita_iva: string, country: string = 'IT'): Promise<VatLookupResult | VatLookupError> {
  try {
    const callable = httpsCallable<{ partita_iva: string; country: string }, {
      valid: boolean;
      ragione_sociale?: string;
      indirizzo_completo?: string;
      parsed?: {
        via?: string;
        cap?: string;
        citta?: string;
        provincia?: string;
        regione?: string;
      };
    }>(functions, 'lookupVatInfo');
    const result = await callable({ partita_iva, country });
    if (!result.data.valid) {
      return { ok: false, error_code: 'lookup.partita_iva.not_valid' };
    }
    return {
      ok: true,
      ragione_sociale: result.data.ragione_sociale,
      indirizzo_completo: result.data.indirizzo_completo,
      parsed: result.data.parsed,
    };
  } catch (err: unknown) {
    const message = (err as { message?: string }).message || 'lookup.internal_error';
    return { ok: false, error_code: message };
  }
}
